"use client";
import React, { useState } from "react";
import { AnimatePresence, LazyMotion, m, domAnimation } from "framer-motion";
import HeroPage2 from "./components/HeroPage2";
import { BranchType } from "../design1/components";
import Switch from "./components/ButtonSwitch";
import CustomSelect from "../design1/components/ui/form/CustomSelect";
import { BranchSummaryCard } from "../design1/Branches";
import { About } from "../design1/About1";
import Link from "next/link";
import { MemberType } from "src/types";
import CardGrid from "../../CardGrid";
import useWindowWidth from "../../hooks/useWindowWidth";
import Skeleton from "react-loading-skeleton";
import { PropertyCardSkeleton } from "./components/Skeletons/Design2PropertyCard";
import Image from "next/image";
import { areAllValuesZero } from "../design1/Staff";
import { generateSlugWithId } from "src/app/serverUtils";
import { useBranchSelection } from "src/app/hooks/useBranchesLogic";
import { VerifiedBadge } from "../../Icon";
const Branches2 = ({ initialData }: { initialData: BranchType }) => {
  const [activeBranch, setActiveBranch] = useState<1 | 2>(1);
  const {
    selectedValue,
    branchOptions,
    branches,
    branchDetails,
    loading,
    handleSelect,
  } = useBranchSelection(initialData);
  return (
    <section className="max-w-full overflow-x-clip">
      <HeroPage2 bgSize="cover" bgImage="/images/meeting-room.webp">
        <div className="text-center flex xs:flex-col xs:items-start xs:gap-5 pb-3 items-center gap-20">
          {loading ? (
            <Skeleton className="max-h-[230px] min-w-[200px] img rounded-xl border-[2px] min-h-[200px] max-w-[400px]" />
          ) : !branchDetails?.picture ? null : (
            <Image
              width={500}
              height={500}
              className="max-h-[230px] min-w-[200px] img rounded-xl border-[2px] overflow-hidden border-white xs:max-w-[280px] sm:h-[130px] xs:!w-[80%] object-cover w-[480px]"
              src={branchDetails.picture}
              alt="branches"
            />
          )}

          <div className="">
            <h1 className=" text-left text-white font-semibold mt-4">
              {loading ? <Skeleton width={150} /> : branchDetails?.branchName}{" "}
            </h1>
            {!branchDetails?.address && !loading ? null : (
              <p className="mt-4 text-2xl xs:text-lg text-white items-start xlg:gap-1 flex gap-2">
                <AddressIcon />
                {loading ? (
                  <Skeleton width={200} />
                ) : (
                  <Link
                    className="hover:underline text-left capitalize"
                    href={`https://maps.google.com/?q=${encodeURIComponent(
                      branchDetails?.address as string
                    )}`}
                  >
                    {branchDetails?.address}
                  </Link>
                )}
              </p>
            )}
          </div>
        </div>
      </HeroPage2>
      <div className="wrapper py-10">
        <div className="first flex xs:flex-col xs:gap-5 xs:items-start xs:justify-start mb-14 items-center justify-between">
          <Switch
            button1Text="Branches"
            button2Text="Staff"
            activeState={activeBranch}
            onSwitch={(newState) => setActiveBranch(newState)}
          />
          {branches && branches.length > 0 && (
            <div className="h-max  p-2 border border-themeColor rounded-md">
              <CustomSelect
                align="left"
                options={branchOptions}
                value={selectedValue}
                onChange={handleSelect}
                placeholder="Select a Branch"
              />
            </div>
          )}
        </div>
        {activeBranch === 1 ? (
          <FirstTab loading={loading} branchDetails={branchDetails} />
        ) : (
          <SecondTab loading={loading} members={branchDetails?.staffs} />
        )}
      </div>
    </section>
  );
};

export default Branches2;

const AddressIcon = () => {
  return (
    <svg
      width="21"
      height="27"
      viewBox="0 0 21 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.1745 15.1381C12.0475 15.1381 13.5658 13.6197 13.5658 11.7468C13.5658 9.87381 12.0475 8.35547 10.1745 8.35547C8.30154 8.35547 6.7832 9.87381 6.7832 11.7468C6.7832 13.6197 8.30154 15.1381 10.1745 15.1381Z"
        stroke="white"
        stroke-width="1.3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M10.1724 2.70117C7.7739 2.70117 5.47366 3.65396 3.77768 5.34995C2.0817 7.04593 1.12891 9.34617 1.12891 11.7446C1.12891 13.8834 1.58334 15.2829 2.82456 16.8316L10.1724 25.3099L17.5202 16.8316C18.7614 15.2829 19.2159 13.8834 19.2159 11.7446C19.2159 9.34617 18.2631 7.04593 16.5671 5.34995C14.8711 3.65396 12.5709 2.70117 10.1724 2.70117Z"
        stroke="white"
        stroke-width="1.3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
export const FirstTab: React.FC<{
  branchDetails?: BranchType;
  loading: boolean;
}> = ({ branchDetails, loading }) => {
  return (
    <div className="grid-container">
      {!loading && !branchDetails?.description ? (
        <div>
          We’re currently updating our branch details. Soon, you’ll be able to
          view the locations, contact information, and services offered by our
          various branches across the region. Please check back shortly for
          the latest updates.
        </div>
      ) : (
        <About
          as="h3"
          loading={loading}
          remove
          title="About Branch"
          desc={branchDetails?.description}
          second
        />
      )}

      {!loading &&
      areAllValuesZero(branchDetails?.branchSummary?.values) ? null : (
        <BranchSummaryCard
          titleAs="h3"
          loading={loading}
          second
          recent
          {...branchDetails?.branchSummary}
        />
      )}
    </div>
  );
};

// Skeleton loader for each card

// The actual component
export const SecondTab: React.FC<{
  members?: MemberType[];
  loading: boolean;
}> = ({ members, loading }) => {
  return (
    <div>
      {loading ? (
        // Display skeleton loaders if loading
        <CardGrid minCardWidth={200} gap={28}>
          {Array(5) // You can adjust the number of skeletons to match how many cards you want
            .fill(null)
            .map((_, index) => (
              <div key={index} className="w-full">
                <PropertyCardSkeleton />
              </div>
            ))}
        </CardGrid>
      ) : members && members.length > 0 ? (
        <CardGrid minCardWidth={200} gap={28}>
          {members.map((item, index) => {
            return (
              <div key={index} className="w-full  h-full">
                <PersonCard {...item} />
              </div>
            );
          })}
        </CardGrid>
      ) : (
        <div>No staff information attached yet. Please try again later.</div>
      )}

      {/* <Button className="block mt-12 mx-auto w-max primary-btn">
        Load More
      </Button> */}
    </div>
  );
};

export const PersonCard: React.FC<MemberType> = ({
  image,
  id,
  name,
  role,
  phone,
  email,
  is_verified,
}) => {
  const slugwithid = generateSlugWithId(Number(id), name);

  const [isHovered, setIsHovered] = useState(false);
  const width = useWindowWidth();
  const isMobile = width < 830;
  return (
    <div
      className="relative bg-themeColor-50 h-full w-full rounded-xl overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Image
        height={200}
        width={200}
        loading="lazy"
        src={image || ""}
        alt={`${name}'s image`}
        className="w-full h-full object-cover"
      />
      <LazyMotion features={domAnimation}>
        <AnimatePresence>
          {(isHovered || isMobile) && (
            <m.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{
                background:
                  "linear-gradient(360deg, #000000 8.84%, rgba(0, 0, 0, 0.5) 27.99%, rgba(0, 0, 0, 0) 60.86%)",
              }}
              className="absolute inset-0 h-full items-center text-white flex flex-col justify-end p-4"
            >
              <h3>
                <Link
                  href={`/staff/${slugwithid}`}
                  className="hover:underline font-semibold gap-2 flex items-center text-xl"
                >
                  {name}{" "}
                  <span className="text-white">
                    {" "}
                    <VerifiedBadge
                      color="white"
                      verified={is_verified as boolean}
                    />
                  </span>
                </Link>
              </h3>
              <h4 className="capitalize h6">{role}</h4>
              {phone && (
                <Link
                  href={`tel:${phone}`}
                  className="hover:underline flex gap-2 items-center"
                >
                  <CallIcon />
                  {phone}
                </Link>
              )}

              <Link
                href={`mailto:${email}`}
                className="hover:underline flex items-center gap-2"
              >
                <MessageIcon />
                <span className="xs:max-w-[200px] max-w-[260px] block overflow-hidden truncate">
                  {email}
                </span>
              </Link>
            </m.div>
          )}
        </AnimatePresence>
      </LazyMotion>
    </div>
  );
};

export const CallIcon = () => {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.86695 1.83332C11.2258 1.9765 12.4951 2.57918 13.4649 3.5417C14.4347 4.50423 15.0469 5.76892 15.2003 7.12665M9.86695 4.49998C10.5226 4.62927 11.1243 4.95267 11.5939 5.42819C12.0634 5.90371 12.3792 6.5094 12.5003 7.16665M15.1669 11.78V13.78C15.1677 13.9657 15.1297 14.1494 15.0553 14.3195C14.9809 14.4897 14.8718 14.6424 14.735 14.7679C14.5982 14.8934 14.4367 14.989 14.2608 15.0485C14.0849 15.1079 13.8985 15.13 13.7136 15.1133C11.6622 14.8902 9.69172 14.1892 7.96028 13.0667C6.3495 12.0431 4.98384 10.6774 3.96028 9.06665C2.83383 7.32734 2.13269 5.34726 1.91361 3.28665C1.89695 3.10229 1.91886 2.91649 1.97795 2.74107C2.03703 2.56564 2.13199 2.40444 2.25679 2.26773C2.38159 2.13102 2.53348 2.02179 2.70281 1.947C2.87213 1.87221 3.05517 1.83349 3.24028 1.83332H5.24028C5.56382 1.83013 5.87748 1.9447 6.12279 2.15567C6.3681 2.36664 6.52833 2.65961 6.57361 2.97998C6.6578 3.62007 6.81436 4.24854 7.04028 4.85332C7.12998 5.09193 7.14939 5.35126 7.09622 5.60057C7.04305 5.84988 6.91952 6.07872 6.74028 6.25998L5.89361 7.10665C6.84265 8.77568 8.22458 10.1576 9.89361 11.1067L10.7403 10.26C10.9215 10.0807 11.1504 9.95722 11.3997 9.90405C11.649 9.85088 11.9083 9.87029 12.1469 9.95998C12.7517 10.1859 13.3802 10.3425 14.0203 10.4267C14.3441 10.4723 14.6399 10.6355 14.8513 10.885C15.0627 11.1345 15.1751 11.453 15.1669 11.78Z"
        stroke="white"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export const MessageIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 4.66659C2 4.31296 2.14048 3.97382 2.39052 3.72378C2.64057 3.47373 2.97971 3.33325 3.33333 3.33325H12.6667C13.0203 3.33325 13.3594 3.47373 13.6095 3.72378C13.8595 3.97382 14 4.31296 14 4.66659V11.3333C14 11.6869 13.8595 12.026 13.6095 12.2761C13.3594 12.5261 13.0203 12.6666 12.6667 12.6666H3.33333C2.97971 12.6666 2.64057 12.5261 2.39052 12.2761C2.14048 12.026 2 11.6869 2 11.3333V4.66659Z"
        stroke="white"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M2 4.6665L8 8.6665L14 4.6665"
        stroke="white"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
